if (!customElements.get('b2h-memorial-decision')) {
  customElements.define('b2h-memorial-decision', class B2HMemorialDecision extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-decision-form]');
      this.result = this.querySelector('[data-decision-result]');
      this.photoChoices = this.querySelector('[data-decision-photo-choices]');
      this.photoChoicesLink = this.querySelector('[data-decision-photo-choices-link]');
      this.sourceField = this.querySelector('[data-decision-source-field]');
      this.sourceError = this.querySelector('[data-decision-source-error]');
      this.submitButton = this.querySelector('[data-decision-submit]');
      this.resetButton = this.querySelector('[data-decision-reset]');
      this.started = false;
      if (!this.form || !this.result) return;

      this.submitButton?.removeAttribute('hidden');
      this.resetButton?.removeAttribute('hidden');
      this.classList.add('is-enhanced');
      this.form.addEventListener('change', (event) => this.onChange(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.addEventListener('reset', () => this.onReset());
      this.result.addEventListener('click', (event) => this.onResultClick(event));
      this.emit('view');
    }

    emit(action, detail = {}) {
      this.dispatchEvent(new CustomEvent('b2h:memorial-decision', {
        bubbles: true,
        detail: { action, ...detail },
      }));
    }

    onChange(event) {
      const control = event.target.closest('[data-decision-question]');
      if (!control || !this.form.contains(control)) return;
      if (this.photoChoicesLink) this.photoChoicesLink.hidden = true;
      if (this.photoChoices) this.photoChoices.open = false;

      if (!this.started) {
        this.started = true;
        this.emit('start');
      }

      if (control.dataset.decisionQuestion === 'identity_source') {
        const sourceControls = [...this.form.querySelectorAll('[data-decision-question="identity_source"]')];
        if (control.checked && control.value === 'not_sure') {
          for (const source of sourceControls) {
            if (source !== control) source.checked = false;
          }
        } else if (control.checked) {
          const notSure = sourceControls.find((source) => source.value === 'not_sure');
          if (notSure) notSure.checked = false;
        }
        this.clearSourceError();
      }

      this.emit('answer', {
        question: control.dataset.decisionQuestion,
        answer: control.value,
        selected: control.type === 'checkbox' ? control.checked : true,
      });
    }

    selectedValue(question) {
      return this.form.querySelector(`[data-decision-question="${question}"]:checked`)?.value || '';
    }

    identitySources() {
      return [...this.form.querySelectorAll('[data-decision-question="identity_source"]:checked')]
        .map((control) => control.value)
        .sort();
    }

    clearSourceError() {
      this.sourceField?.removeAttribute('aria-invalid');
      if (this.sourceError) this.sourceError.hidden = true;
    }

    validateSources() {
      const sources = this.identitySources();
      if (sources.length > 0) {
        this.clearSourceError();
        return true;
      }
      this.sourceField?.setAttribute('aria-invalid', 'true');
      if (this.sourceError) this.sourceError.hidden = false;
      this.form.querySelector('[data-decision-question="identity_source"]')?.focus();
      return false;
    }

    outcomeFor({ buyerRole, moment, identitySources, preferredPresence }) {
      if (buyerRole === 'supporting_someone' && moment === 'recent_loss') return 'recipient_control';
      if (moment === 'capture_now' || identitySources.includes('existing_print')) return 'preservation_first';
      if (preferredPresence === 'private' || preferredPresence === 'not_sure') return 'private_no_purchase';
      if (
        preferredPresence === 'daily_home'
        && identitySources.includes('name_dates')
        && this.dataset.currentProductAvailable === 'true'
      ) return 'current_home_product';
      if (identitySources.includes('photo') && preferredPresence === 'portable') return 'portrait_checklist';
      if (buyerRole === 'supporting_someone' || preferredPresence === 'recipient_choice') return 'recipient_control';
      if (identitySources.includes('photo')) return 'portrait_checklist';
      return 'private_no_purchase';
    }

    onSubmit(event) {
      event.preventDefault();
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }
      if (!this.validateSources()) return;

      const answers = {
        buyerRole: this.selectedValue('buyer_role'),
        moment: this.selectedValue('moment'),
        identitySources: this.identitySources(),
        preferredPresence: this.selectedValue('preferred_presence'),
      };
      const outcome = this.outcomeFor(answers);
      const showPhotoChoices = answers.buyerRole === 'self'
        && answers.moment === 'remember_later'
        && answers.identitySources.length === 1
        && answers.identitySources[0] === 'photo'
        && answers.preferredPresence === 'daily_home';
      if (this.photoChoicesLink) this.photoChoicesLink.hidden = !showPhotoChoices;

      for (const panel of this.result.querySelectorAll('[data-decision-outcome]')) {
        panel.hidden = panel.dataset.decisionOutcome !== outcome;
      }
      this.result.hidden = false;
      this.emit('complete', {
        buyer_role: answers.buyerRole,
        moment: answers.moment,
        identity_sources: answers.identitySources,
        preferred_presence: answers.preferredPresence,
        outcome_key: outcome,
      });
      this.emit('outcome_view', { outcome_key: outcome });
      this.result.querySelector(`[data-decision-outcome="${outcome}"] [data-decision-result-heading]`)
        ?.focus({ preventScroll: false });
    }

    onResultClick(event) {
      const photoLink = event.target.closest('[data-decision-photo-choices-link]');
      if (photoLink && this.result.contains(photoLink) && this.photoChoices) {
        event.preventDefault();
        this.photoChoices.open = true;
        this.photoChoices.querySelector('summary')?.focus();
        return;
      }
      const link = event.target.closest('[data-decision-action]');
      if (!link || !this.result.contains(link)) return;
      const panel = link.closest('[data-decision-outcome]');
      this.emit(link.dataset.decisionAction === 'primary' ? 'primary_click' : 'secondary_click', {
        outcome_key: panel?.dataset.decisionOutcome,
        destination_path: link.dataset.decisionDestination,
      });
    }

    onReset() {
      requestAnimationFrame(() => {
        if (this.photoChoicesLink) this.photoChoicesLink.hidden = true;
        if (this.photoChoices) this.photoChoices.open = false;
        this.clearSourceError();
        for (const panel of this.result.querySelectorAll('[data-decision-outcome]')) panel.hidden = true;
        this.result.hidden = true;
      });
    }
  });
}
